import { cx } from "class-variance-authority";
import { useEffect, useRef, useState } from "react";

import { SearchIcon } from "@/assets/icons/general/Search";
import { TextInput } from "@/components/ui/inputs/TextInput/TextInput";
import { Typography } from "@/components/ui/text/Typography/Typography";

import { SearchSection } from "./SearchSection";

interface SearchJobsProps {
  search: string;
  setSearch: (search: string) => void;
  onFocusChange?: (isFocused: boolean) => void;
}

export const SearchJobs = ({ search, setSearch, onFocusChange }: SearchJobsProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="mx-auto flex max-w-[460px] flex-col gap-10">
      <div
        className={cx(
          "flex flex-col justify-center gap-1-5 overflow-hidden transition-all duration-500 ease-out",
          isFocused || search.length
            ? "-translate-y-2.5 pointer-events-none max-h-0 opacity-0"
            : "max-h-40 opacity-100",
        )}
      >
        <Typography
          size="title-4"
          className="!font-labels-prominent-1 text-center"
        >
          Find job positions and optimal value for your next hire
        </Typography>
        <Typography
          size="body-2"
          className="text-center text-text-inverted-secondary"
        >
          Stay informed on salary trends and fluctuations to make the best decisions for your next hire.
        </Typography>
      </div>
      <TextInput
        icon={SearchIcon}
        placeholder="Search for job positions"
        hideLabel
        label="Search"
        variant="search"
        className="max-w-md"
        value={search}
        onFocus={() => {
          // Clear any pending blur timeout
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          setIsFocused(true);
          setIsSearchFocused(true);
          onFocusChange?.(true);
        }}
        onBlur={() => {
          // Clear any existing timeout before setting a new one
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
          }
          // Delay the blur to allow clicks on table rows to complete
          blurTimeoutRef.current = setTimeout(() => {
            setIsFocused(false);
            setIsSearchFocused(false);
            onFocusChange?.(false);
          }, 200);
        }}
        onChange={(e) => setSearch(e.target.value)}
      />
      <SearchSection
        isHidden={!isFocused && !search.length}
        isFocusedOnly={isSearchFocused && !search.length}
        searchTerm={search}
      />
    </div>
  );
};
